/**
 * Role Assumption Context
 *
 * Allows Platform Admins to assume different user roles for testing/viewing.
 * This is session-only state - NOT persisted to Firestore.
 *
 * Features:
 * - Session-based role switching
 * - Only Platform Admins can assume roles
 * - Organization selection when assuming org-based roles
 * - Audit logging (console for now)
 * - effectiveRole computed property
 */

import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';
import { UserRole, Organization } from '../services/firestore';
import { useAuth } from './AuthContext';

// Role labels for display
export const ROLE_LABELS: Record<UserRole, string> = {
  platform_admin: 'roles.platformAdmin',
  cra_admin: 'roles.craAdmin',
  cra_analyst: 'roles.craAnalyst',
  rating_committee: 'roles.ratingCommittee',
  bank_admin: 'roles.bankAdmin',
  bank_user: 'roles.bankUser',
};

// Roles that require an organization to be selected
export const ORG_REQUIRED_ROLES: UserRole[] = ['cra_admin', 'cra_analyst', 'rating_committee', 'bank_admin', 'bank_user'];

interface AssumedRole {
  role: UserRole;
  assumedAt: Date;
  originalRole: UserRole;
  organization?: Organization | null; // The organization being viewed as
}

interface RoleAssumptionContextType {
  // State
  assumedRole: AssumedRole | null;
  isAssuming: boolean;
  assumedOrganization: Organization | null;

  // Actions (Platform Admin only)
  assumeRole: (role: UserRole, organization?: Organization | null) => void;
  exitAssumedRole: () => void;

  // Computed
  effectiveRole: UserRole | null;
  effectiveOrganization: Organization | null; // Returns assumed org or actual org
  canAssumeRoles: boolean;

  // Effective role checks (use these for UI/routing)
  effectiveIsPlatformAdmin: boolean;
  effectiveIsCRAAdmin: boolean;
  effectiveIsCRAAnalyst: boolean;
  effectiveIsRatingCommittee: boolean;
  effectiveIsBankAdmin: boolean;
  effectiveIsBankUser: boolean;
}

const RoleAssumptionContext = createContext<RoleAssumptionContextType | undefined>(undefined);

// Safe defaults when provider is missing (on-prem mode skips RoleAssumptionProvider)
const NO_OP_DEFAULTS: RoleAssumptionContextType = {
  assumedRole: null,
  isAssuming: false,
  assumedOrganization: null,
  assumeRole: () => {},
  exitAssumedRole: () => {},
  effectiveRole: null,
  effectiveOrganization: null,
  canAssumeRoles: false,
  effectiveIsPlatformAdmin: false,
  effectiveIsCRAAdmin: false,
  effectiveIsCRAAnalyst: false,
  effectiveIsRatingCommittee: false,
  effectiveIsBankAdmin: false,
  effectiveIsBankUser: false,
};

export const useRoleAssumption = (): RoleAssumptionContextType => {
  const context = useContext(RoleAssumptionContext);
  const { role, organization } = useAuth();
  if (context === undefined) {
    // In on-prem mode the provider is skipped; pass through actual auth values
    // so all consumers see the real role without needing individual fallbacks.
    return {
      ...NO_OP_DEFAULTS,
      effectiveRole: role,
      effectiveOrganization: organization,
      effectiveIsPlatformAdmin: role === 'platform_admin',
      effectiveIsCRAAdmin: role === 'cra_admin',
      effectiveIsCRAAnalyst: role === 'cra_analyst',
      effectiveIsRatingCommittee: role === 'rating_committee',
      effectiveIsBankAdmin: role === 'bank_admin',
      effectiveIsBankUser: role === 'bank_user',
    };
  }
  return context;
};

interface RoleAssumptionProviderProps {
  children: React.ReactNode;
}

/**
 * Log role assumption events for audit trail
 */
const logRoleAssumption = (
  action: 'assume' | 'exit',
  details: {
    userId?: string;
    originalRole?: UserRole | null;
    assumedRole?: UserRole;
    assumedOrgId?: string;
    assumedOrgName?: string;
    timestamp: string;
  }
) => {
  console.log('[RoleAssumption]', {
    action,
    ...details,
  });
};

export const RoleAssumptionProvider: React.FC<RoleAssumptionProviderProps> = ({ children }) => {
  const { role: actualRole, userProfile, isPlatformAdmin, organization: actualOrganization } = useAuth();
  const [assumedRole, setAssumedRole] = useState<AssumedRole | null>(null);

  // Only platform admins can assume roles
  const canAssumeRoles = isPlatformAdmin;

  // Assume a different role (with optional organization for org-based roles)
  const assumeRole = useCallback(
    (roleToAssume: UserRole, organization?: Organization | null) => {
      if (!canAssumeRoles) {
        console.warn('[RoleAssumption] Only Platform Admins can assume roles');
        return;
      }

      if (!actualRole) {
        console.warn('[RoleAssumption] Cannot assume role - no current role');
        return;
      }

      // If assuming the same as actual role with no org change, just exit assumption
      if (roleToAssume === actualRole && !organization) {
        setAssumedRole(null);
        logRoleAssumption('exit', {
          userId: userProfile?.uid,
          originalRole: actualRole,
          timestamp: new Date().toISOString(),
        });
        return;
      }

      const newAssumedRole: AssumedRole = {
        role: roleToAssume,
        assumedAt: new Date(),
        originalRole: actualRole,
        organization: organization || null,
      };

      setAssumedRole(newAssumedRole);
      logRoleAssumption('assume', {
        userId: userProfile?.uid,
        originalRole: actualRole,
        assumedRole: roleToAssume,
        assumedOrgId: organization?.id,
        assumedOrgName: organization?.name,
        timestamp: new Date().toISOString(),
      });
    },
    [canAssumeRoles, actualRole, userProfile?.uid]
  );

  // Exit assumed role and return to original
  const exitAssumedRole = useCallback(() => {
    if (assumedRole) {
      logRoleAssumption('exit', {
        userId: userProfile?.uid,
        originalRole: assumedRole.originalRole,
        assumedRole: assumedRole.role,
        timestamp: new Date().toISOString(),
      });
    }
    setAssumedRole(null);
  }, [assumedRole, userProfile?.uid]);

  // Computed values
  const isAssuming = assumedRole !== null;
  const effectiveRole = isAssuming ? assumedRole.role : actualRole;
  const assumedOrganization = assumedRole?.organization || null;

  // Effective organization: use assumed org when assuming, otherwise actual org
  const effectiveOrganization = isAssuming ? assumedOrganization : actualOrganization;

  // Effective role checks
  const effectiveIsPlatformAdmin = effectiveRole === 'platform_admin';
  const effectiveIsCRAAdmin = effectiveRole === 'cra_admin';
  const effectiveIsCRAAnalyst = effectiveRole === 'cra_analyst';
  const effectiveIsRatingCommittee = effectiveRole === 'rating_committee';
  const effectiveIsBankAdmin = effectiveRole === 'bank_admin';
  const effectiveIsBankUser = effectiveRole === 'bank_user';

  const value = useMemo<RoleAssumptionContextType>(
    () => ({
      assumedRole,
      isAssuming,
      assumedOrganization,
      assumeRole,
      exitAssumedRole,
      effectiveRole,
      effectiveOrganization,
      canAssumeRoles,
      effectiveIsPlatformAdmin,
      effectiveIsCRAAdmin,
      effectiveIsCRAAnalyst,
      effectiveIsRatingCommittee,
      effectiveIsBankAdmin,
      effectiveIsBankUser,
    }),
    [
      assumedRole,
      isAssuming,
      assumedOrganization,
      assumeRole,
      exitAssumedRole,
      effectiveRole,
      effectiveOrganization,
      canAssumeRoles,
      effectiveIsPlatformAdmin,
      effectiveIsCRAAdmin,
      effectiveIsCRAAnalyst,
      effectiveIsRatingCommittee,
      effectiveIsBankAdmin,
      effectiveIsBankUser,
    ]
  );

  return (
    <RoleAssumptionContext.Provider value={value}>
      {children}
    </RoleAssumptionContext.Provider>
  );
};

export default RoleAssumptionContext;
